/**
 * Active-room context/provider.
 *
 * Holds the currently-active room so downstream features (Tasks, Estimation)
 * can scope their work to a single workspace. The selected room id is persisted
 * to localStorage so it survives reloads, and the full Room document is lazily
 * hydrated through the {@link RoomStore} (facade only — strict layering).
 *
 * Consumers use {@link useActiveRoom}; pages/features never touch the store or
 * the api facade directly for this.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useApi } from '../../hooks/useApi';
import { getActiveTeamBundleKey } from '../tenant/activeTenant';
import { RoomStore } from './roomStore';
import type { Room } from './types';

const STORAGE_KEY_PREFIX = 'ev.activeRoomId';

/**
 * The localStorage key for the active-room selection, namespaced by the current
 * tenant's bundle key so switching teams never hydrates a foreign/stale room
 * id. The built-in workspace (empty bundle key) gets its own `…::` slot.
 */
function storageKey(): string {
  return `${STORAGE_KEY_PREFIX}::${getActiveTeamBundleKey()}`;
}

export interface ActiveRoomContextValue {
  /** The id of the active room, or null when none is selected. */
  activeRoomId: string | null;
  /** The hydrated active room document, or null. */
  activeRoom: Room | null;
  /** True while the active room document is being (re)loaded. */
  loading: boolean;
  /** Selects (or clears, with null) the active room and persists the choice. */
  setActiveRoomId: (id: string | null) => void;
  /** Re-hydrates the active room document from the store. */
  refreshActiveRoom: () => Promise<void>;
}

const ActiveRoomContext = createContext<ActiveRoomContextValue | null>(null);

function readStoredId(): string | null {
  try {
    return window.localStorage.getItem(storageKey());
  } catch {
    return null;
  }
}

export function ActiveRoomProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const store = useMemo(() => new RoomStore(api), [api]);

  const [activeRoomId, setActiveRoomIdState] = useState<string | null>(
    readStoredId,
  );
  const [activeRoom, setActiveRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(false);

  // Guards against out-of-order async loads overwriting fresher state.
  const loadSeq = useRef(0);

  const setActiveRoomId = useCallback((id: string | null) => {
    setActiveRoomIdState(id);
    try {
      if (id) window.localStorage.setItem(storageKey(), id);
      else window.localStorage.removeItem(storageKey());
    } catch {
      /* storage unavailable — in-memory selection still works */
    }
  }, []);

  const load = useCallback(
    async (id: string | null) => {
      const seq = ++loadSeq.current;
      if (!id) {
        setActiveRoom(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const room = await store.get(id);
        if (seq !== loadSeq.current) return; // superseded
        // Treat missing/soft-deleted rooms as "no active room".
        setActiveRoom(room && !room.deleted ? room : null);
      } catch {
        if (seq === loadSeq.current) setActiveRoom(null);
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    },
    [store],
  );

  useEffect(() => {
    void load(activeRoomId);
  }, [activeRoomId, load]);

  const refreshActiveRoom = useCallback(
    () => load(activeRoomId),
    [load, activeRoomId],
  );

  const value = useMemo<ActiveRoomContextValue>(
    () => ({
      activeRoomId,
      activeRoom,
      loading,
      setActiveRoomId,
      refreshActiveRoom,
    }),
    [activeRoomId, activeRoom, loading, setActiveRoomId, refreshActiveRoom],
  );

  return (
    <ActiveRoomContext.Provider value={value}>
      {children}
    </ActiveRoomContext.Provider>
  );
}

/**
 * Returns the active-room context. Throws if used outside
 * {@link ActiveRoomProvider}.
 */
export function useActiveRoom(): ActiveRoomContextValue {
  const ctx = useContext(ActiveRoomContext);
  if (!ctx) {
    throw new Error('useActiveRoom must be used within an <ActiveRoomProvider>');
  }
  return ctx;
}
